// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Configuration for accounting form fields including labels and tooltips.
 * This module provides centralized field definitions used across asset and
 * product accounting forms to ensure consistency.
 */

/**
 * Minimum allowed useful life in months for assets and products.
 * Useful life must be at least 1 month.
 */
export const MIN_USEFUL_LIFE_MONTHS = 1;

/**
 * Maximum allowed useful life in months for assets and products.
 * This is set to 480 months (40 years), which is the maximum depreciation
 * period allowed by the IRS for real property under U.S. tax code.
 */
export const MAX_USEFUL_LIFE_MONTHS = 480;

/**
 * Configuration for a single accounting form field.
 */
interface AccountingFieldConfig {
  /**
   * Display label for the form field
   */
  label: string;
  /**
   * Tooltip text providing additional context and guidance
   */
  tooltip: string;
}

/**
 * Field configurations for cost breakdown components.
 * Used by both asset and product accounting forms.
 */
export const COST_BREAKDOWN_FIELDS = {
  billOfMaterials: {
    label: 'Equipment or materials/BOM cost ($)',
    tooltip:
      'Cost of purchased devices, raw materials, parts, or assemblies that make up the asset',
  },
  directLabor: {
    label: 'Direct assembly labor ($)',
    tooltip:
      'Labor directly attributable to assembling or preparing the asset before deployment',
  },
  allocatedIndirect: {
    label: 'Indirect allocated overhead ($)',
    tooltip:
      'Indirect overhead tied to asset acquisition or preparation, such as supervision, facilities, and utilities',
  },
  freightInbound: {
    label: 'Inbound freight cost ($)',
    tooltip:
      'Transportation costs to bring materials or asset into your warehouse or staging site',
  },
  freightOutbound: {
    label: 'Outbound freight cost ($)',
    tooltip:
      'Transportation costs to deliver the asset from your warehouse or staging site to its operating location in preparation for deployment',
  },
  installation: {
    label: 'Installation and setup cost ($)',
    tooltip:
      'Setup, site preparation, configuration, commissioning, and testing costs to prepare the asset for deployment and make it operational',
  },
  tax: {
    label: 'Acquisition taxes ($)',
    tooltip:
      'Non-recoverable taxes (e.g., sales tax, use tax, or import VAT) paid on acquisition of the asset, materials, or parts',
  },
  tariffs: {
    label: 'Import duties and tariffs ($)',
    tooltip:
      'Customs duties, tariffs, and import fees on foreign-sourced assets, materials, or parts',
  },
  interest: {
    label: 'Capitalizable interest ($)',
    tooltip:
      'Capitalizable financing costs incurred during construction or long deployment projects',
  },
} as const satisfies Record<string, AccountingFieldConfig>;
